import { useCallback, useEffect, useMemo, useState } from "react"
import { Virtuoso } from "react-virtuoso"
import { dateFormat, numberFormat, safeParseDate } from "shared/util"

import { Box, Button, Divider, IconButton, Slide, Stack, Typography } from "@mui/material"
import {
	Replay as ReplayIcon,
	ArrowRight as OpenIcon
} from '@mui/icons-material'

import { GridColumnDef, GridColumnDefMap, GridFilter, GridFilterDateRange } from "../../types"
import { isFilterd } from "../../util"

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateField } from '@mui/x-date-pickers/DateField'

import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/ja'

dayjs.locale('ja')

type Histgram = {
	year: number,
	count: number
	rate: number
	isOpen: boolean
	months: {
		month: number
		count: number
		rate: number
	}[]
}

export const buildFilterLabel = (
	range: GridFilterDateRange
) => {
	const labels = []
	if (range.from) {
		labels.push(dateFormat(new Date(range.from), { wareki: false }))
	}
	labels.push('～')
	if (range.to) {
		labels.push(dateFormat(new Date(range.to), { wareki: false }))
	}
	return labels.join('')
}

type DateFilterProps<T> = {
	field: string
	fieldDef: GridColumnDef<T>
	columnsDef: GridColumnDefMap<T>
	dataList: T[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	values: any[] | null
	filter: GridFilter | undefined
	filters: GridFilter[]
	setFilters: (filters: GridFilter[]) => void
}
export const DateFilter = <T,>({
	field,
	fieldDef,
	columnsDef,
	dataList,
	values,
	filter,
	filters,
	setFilters
}: DateFilterProps<T>) => {
	const [histgram, setHistgram] = useState<Histgram[]>([])
	const [isDragging, setIsDragging] = useState<boolean>(false)
	const [dragFrom, setDragFrom] = useState<number | null>(null)
	const [dragTo, setDragTo] = useState<number | null>(null)
	const [from, setFrom] = useState<Dayjs | null>(null)
	const [to, setTo] = useState<Dayjs | null>(null)

	useEffect(
		() => {
			const range = filter?.dateRange
			setFrom(range?.from ? dayjs(new Date(range.from)) : null)
			setTo(range?.to ? dayjs(new Date(range.to)) : null)
		},
		[filter]
	)
	const setRangeDirect = useCallback(
		(dateRange: GridFilterDateRange) => {
			if (filter) {
				if (!dateRange.to && !dateRange.from) {
					// remove filter
					setFilters(filters.filter(f => f.field !== filter.field).slice())
				} else {
					// replace filter
					const newFilter: GridFilter = {
						...filter,
						label: buildFilterLabel(dateRange),
						dateRange
					}
					setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
				}
			} else if (dateRange.from || dateRange.to) {
				// new filter
				setFilters(filters.concat({
					field,
					label: buildFilterLabel(dateRange),
					dateRange
				}).slice())
			}
		},
		[setFilters, filters, filter, field]
	)

	const setFromValue = useCallback(
		(value: Dayjs | null) => {
			const date = value ? new Date(value.year(), value.month(), value.date()) : undefined
			const range = filter?.dateRange
			setRangeDirect({ ...range, from: date?.getTime() })
			setFrom(value)
		},
		[filter, setRangeDirect]
	)
	const onResetFrom = useCallback(
		() => {
			const dateRange = filter?.dateRange
			if (!dateRange) return
			setRangeDirect({ ...dateRange, from: undefined })
		},
		[filter, setRangeDirect]
	)

	const setToValue = useCallback(
		(value: Dayjs | null) => {
			const date = value ? new Date(value.year(), value.month(), value.date()) : undefined
			const range = filter?.dateRange
			setRangeDirect({ ...range, to: date?.getTime() })
			setTo(value)
		},
		[filter, setRangeDirect]
	)
	const onResetTo = useCallback(
		() => {
			const dateRange = filter?.dateRange
			if (!dateRange) return
			setRangeDirect({ ...dateRange, to: undefined })
		},
		[filter, setRangeDirect]
	)

	useEffect(
		() => {
			setHistgram(prev => {
				const histgramRaw: {
					year: number
					month: number
					count: number
				}[] = []

				let maxCountOfYear = 0
				let maxCountOfMonth = 0
				let minDate: Date | null = null
				let maxDate: Date | null = null
				for(const record of dataList) {
					if (filters.some(filter => filter.field === field ? false : isFilterd(record, filter, columnsDef))) continue

					const value = fieldDef.value(record) ?? 0
					if (typeof value !== 'string') continue

					const date = safeParseDate(value)
					if (!date) continue

					const year = date.getFullYear()
					const month = date.getMonth()
					const histgramRecord = histgramRaw.find(h => h.year === year && h.month === month)
					if (!histgramRecord) {
						histgramRaw.push({ year, month, count: 1 })
						if (maxCountOfMonth === 0) maxCountOfMonth = 1
					} else {
						histgramRecord.count = histgramRecord.count + 1
						if (maxCountOfMonth < histgramRecord.count) maxCountOfMonth = histgramRecord.count
					}
					if (!minDate || minDate.getTime() > date.getTime()) minDate = date
					if (!maxDate || maxDate.getTime() < date.getTime()) maxDate = date
				}
				if (!minDate || !maxDate) return []

				const histgram: Histgram[] = []
				for(let year = minDate.getFullYear(); year <= maxDate.getFullYear(); year++) {
					const monthStart = year === minDate.getFullYear() ? minDate.getMonth() : 0
					const monthEnd = year === maxDate.getFullYear() ? maxDate.getMonth() : 11
					const histgramYear: Histgram = {
						year,
						count: 0,
						rate: 0,
						isOpen: prev.find(pre => pre.year === year)?.isOpen ?? false,
						months: []
					}
					for(let month = monthStart; month <= monthEnd; month++) {
						const histgramRecord = histgramRaw.find(h => h.year === year && h.month === month)
						if (histgramRecord) {
							const count = histgramRecord.count
							const rate = Math.ceil((count * 100 * 100) / maxCountOfMonth) / 100
							histgramYear.months.unshift({ month, count, rate })
							histgramYear.count = histgramYear.count + count
						} else {
							histgramYear.months.unshift({ month, count: 0, rate: 0 })
						}
					}
					if (maxCountOfYear < histgramYear.count) maxCountOfYear = histgramYear.count
					histgram.unshift(histgramYear)
				}
				histgram.forEach(yearRecord => yearRecord.rate = Math.ceil((yearRecord.count * 100 * 100) / maxCountOfYear) / 100)
				return histgram
			})
		},
		[setHistgram, fieldDef, dataList, filters, columnsDef, field]
	)

	const toggleOpenYear = useCallback(
		(year: number) => {
			setHistgram(prev => [...prev.map(
				r => r.year === year ? { ...r, isOpen: !r.isOpen } : r
			)])
		},
		[setHistgram]
	)

	const histgramIndex: {
		year: number
		isOpen?: boolean
		month?: number
		count: number
		rate: number
	}[] = useMemo(
		() => histgram.flatMap(yearRecord => {
			const yearIndex = {
				year: yearRecord.year,
				isOpen: yearRecord.isOpen,
				count: yearRecord.count,
				rate: yearRecord.rate
			}
			if (!yearRecord.isOpen) return yearIndex
			return [yearIndex, ...yearRecord.months.map(monthRecord => ({
				year: yearRecord.year,
				month: monthRecord.month,
				count: monthRecord.count,
				rate: monthRecord.rate
			}))]
		}),
		[histgram]
	)

	const isCurrent = useCallback(
		(year: number, month: number | undefined) => values?.some(value => {
			if (typeof value !== 'string') return false
			const date = safeParseDate(value)
			if (!date) return false
			if (month === undefined) {
				return (date.getFullYear() === year)
			} else {
				return (date.getFullYear() === year && date.getMonth() === month)
			}
		}) ?? false,
		[values]
	)
	const dragStart = useCallback(
		(startIndex: number) => {
			setDragFrom(startIndex)
			setDragTo(startIndex)
			setIsDragging(true)
		},
		[setDragFrom, setDragTo, setIsDragging]
	)
	const dragEnter = useCallback(
		(enterIndex: number) => {
			if (!isDragging || dragFrom === null) return
			setDragTo(enterIndex)
		},
		[isDragging, dragFrom, setDragTo]
	)
	const handleMouseUp = useCallback(
		() => {
			if (isDragging && dragFrom !== null && dragTo !== null) {
				const { fromIndex, toIndex } = dragFrom > dragTo ?
					{ fromIndex: dragFrom, toIndex: dragTo } : { fromIndex: dragTo, toIndex: dragFrom }

				const fromRecord = fromIndex >= 0 ? histgramIndex[fromIndex] : undefined
				const fromDate = fromRecord ? new Date(fromRecord.year, fromRecord.month ?? 0, 1) : undefined
				const toRecord = toIndex >= 0 ? histgramIndex[toIndex] : undefined
				const toDate = toRecord ? new Date(toRecord.year, (toRecord.month ?? 11) + 1, 0) : undefined
				const dateRange: GridFilterDateRange = {
					from: fromDate?.getTime(),
					to: toDate?.getTime()
				}				
				if (filter) {
					if (!dateRange.from && !dateRange.to) {
						// remove filter
						setFilters(filters.filter(f => f.field !== filter.field).slice())
					} else {
						// replace filter
						const newFilter: GridFilter = {
							...filter,
							label: buildFilterLabel(dateRange),
							dateRange
						}
						setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
					}
				} else if (dateRange.from || dateRange.to) {
					// new filter
					setFilters(filters.concat({
						field,
						label: buildFilterLabel(dateRange),
						dateRange
					}).slice())
				}
			}
			setIsDragging(false)
		},
		[setIsDragging, dragFrom, dragTo, field, filter, filters, histgramIndex, isDragging, setFilters]
	)
	useEffect(
		() => {
			document.addEventListener('mouseup', handleMouseUp)
			return () => {
				document.removeEventListener('mouseup', handleMouseUp)
			}
		},
		[handleMouseUp]
	)
	return (
		<>
			<Stack direction="row" spacing={1} padding={1}>
				<LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ year: 'YYYY年' }}>
					<Stack direction="column" spacing={1} alignItems="center" flexGrow={1} overflow='hidden'>
						<DateField
							size="small"
							format="YYYY/MM/DD"
							value={from}
							onChange={(newValue) => setFromValue(newValue) }
						/>
						<Button
							fullWidth
							disabled={filter === undefined || filter.dateRange?.from === undefined}
							variant="outlined"
							size="small"
							startIcon={<ReplayIcon />}
							sx={{ whiteSpace: 'nowrap' }}
							onClick={onResetFrom}
						>
							初期状態に戻す
						</Button>
					</Stack>
					<Stack direction="column" spacing={1} alignItems="center" flexGrow={1} overflow='hidden'>
						<DateField
							size="small"
							format="YYYY/MM/DD"
							value={to}
							onChange={(newValue) => setToValue(newValue) }
						/>
						<Button
							fullWidth
							disabled={filter === undefined || filter.dateRange?.to === undefined}
							variant="outlined"
							size="small"
							startIcon={<ReplayIcon />}
							sx={{ whiteSpace: 'nowrap' }}
							onClick={onResetTo}
						>
							初期状態に戻す
						</Button>
					</Stack>
				</LocalizationProvider>
			</Stack>

			<Divider/>

			<Box flexGrow={1} overflow='auto' boxSizing="border-box" width='100%'>
				<Virtuoso
					data={histgramIndex}
					itemContent={(i, row) => (
						<Box
							key={row.month ? `histgram-${row.year}-${row.month}` : `histgram-${row.year}`}
							padding={0.5}
							onMouseDown={(ev) => {
								ev.stopPropagation()
								dragStart(i)
								ev.preventDefault()
							}}
							onMouseEnter={() => dragEnter(i)}
							sx={{
								"transition": "background 0.05s",
								"bgcolor": isDragging && dragFrom !== null && dragTo !== null && (
									dragFrom > dragTo ? (dragFrom >= i && dragTo <= i) : (dragTo >= i && dragFrom <= i)
								) ? "rgba(0, 0, 0, 0.04)" : undefined,
								'&:hover': {
									bgcolor: "rgba(0, 0, 0, 0.04)"
								},
								"cursor": "pointer"
							}}
						>
							<Box padding={0.5} position='relative' boxSizing='border-box' width="100%" overflow="hidden">
								<Stack direction="row" alignItems="center" position='relative' spacing={0.5} zIndex={1}>
									{row.month === undefined ? (
										<>
											<IconButton
												size="small"
												sx={{ padding: 0 }}
												onClick={(ev) => {
													ev.stopPropagation()
													toggleOpenYear(row.year)
													ev.preventDefault()
												}}
											>
												<OpenIcon fontSize="inherit" style={{
													transform: row.isOpen === true ? 'rotate(90deg)' : 'rotate(0deg)',
													transition: 'transform 0.2s ease-in-out'
												}} />
											</IconButton>

											<Typography variant="body2">{`${row.year}`}</Typography>
											<Typography variant="caption">年</Typography>
										</>
									) : (
										<>
											<Box width='2rem'/>
											<Typography variant="body2">{`${row.month+1}`}</Typography>
											<Typography variant="caption">月</Typography>
										</>
									)}
									<Box flexGrow={1}/>
									<Typography variant="caption" whiteSpace="nowrap">{numberFormat(row.count)}件</Typography>
								</Stack>
								<Slide direction="left" in>
									<div
										key={`histgram-bar-${row.year}-${row.month}`}
										style={{
											position: 'absolute',
											right: 0,
											top: 0, bottom: 0,
											width: `${row.rate}%`,
											background: isCurrent(row.year, row.month) ? 'rgb(225,225,245)' : 'rgb(235,235,235)',
											borderRadius: '4px 0 0 4px',
											transition: 'width 0.3s, background 0.3s',
											zIndex: 0
										}}
									/>
								</Slide>
							</Box>
						</Box>
					)}
				/>
			</Box>
		</>
	)
}

